import React, { useCallback, useEffect } from 'react';
import { cn } from '@/lib/utils';
import { AlignJustify } from 'lucide-react';
import { LegendHighlightInfo } from '@/services/highlightProcessingService';
import { HighlightTooltip } from './HighlightTooltip';
import { HighlightNoteContent } from './HighlightNoteContent';
import { useHighlightDebug } from '@/contexts/HighlightDebugContext';

// Define the props interface for the refined component
interface HighlightLegendProps {
  legendItems: LegendHighlightInfo[];
  showHighlights: boolean;
  activeHighlightId?: string;
  onHighlightInteraction?: (highlightId: string, action: 'enter'|'leave'|'click'|'scroll') => void;
}

// Helper function to add alpha to HSL color string
function hslToHsla(hslColor: string, alpha: number): string {
  if (!hslColor || !hslColor.startsWith('hsl(')) return hslColor; // Return original if not valid HSL
  // Replace 'hsl(' with 'hsla(' and add alpha before the closing parenthesis
  return hslColor.replace('hsl(', 'hsla(').replace(')', `, ${alpha})`);
}

/**
 * Refined HighlightLegend component that displays all highlights in a categorized legend.
 * This version uses pre-processed highlight data from the centralized highlight service.
 * Features:
 * - Categorizes highlights into style-specific and general literary techniques
 * - Displays multi-line indicators for highlights that span multiple lines
 * - Uses the unified tooltip system for consistency
 * - Provides scroll-to functionality to navigate to highlights in the text
 * - Maintains accessibility with keyboard navigation and proper ARIA attributes
 */
export const HighlightLegend: React.FC<HighlightLegendProps> = ({
  legendItems,
  showHighlights,
  activeHighlightId,
  onHighlightInteraction
}) => {
  // Connect with highlight debug context if available
  let highlightDebug = undefined;
  try {
    highlightDebug = useHighlightDebug();
  } catch (error) {
    // Context not available, continue without it
  }
  
  // If the highlight functionality is disabled or no highlights exist, don't render
  if (!showHighlights || legendItems.length === 0) {
    return null;
  }

  // Send data to debug context when it changes
  useEffect(() => {
    if (highlightDebug?.isDebugPanelVisible && typeof highlightDebug.captureHighlightData === 'function') {
      highlightDebug.captureHighlightData([], legendItems.map(item => item.highlight));
    }
  }, [highlightDebug, legendItems, highlightDebug?.isDebugPanelVisible]);

  // Function to scroll to highlight in poem
  const scrollToHighlight = useCallback((highlightId: string) => {
    // Find the highlight element in the document
    const highlightElement = document.querySelector(
      `[data-highlight-id="${highlightId}"][data-position-type="start"], [data-highlight-id="${highlightId}"][data-position-type="single"]`
    );
    
    if (highlightElement) {
      // Scroll into view with smooth behavior
      highlightElement.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'center'
      });
      
      // Add temporary focus ring for visual feedback
      highlightElement.classList.add('highlight-focused');
      setTimeout(() => {
        highlightElement.classList.remove('highlight-focused');
      }, 2000);
    }
  }, []);

  // Handler for various highlight interactions
  const handleHighlightInteraction = useCallback((highlightId: string, action: 'enter'|'leave'|'click'|'scroll') => {
    if (onHighlightInteraction) {
      onHighlightInteraction(highlightId, action);
    }
    
    // If action is scroll, also trigger scrolling
    if (action === 'scroll') {
      scrollToHighlight(highlightId);
    }
  }, [onHighlightInteraction, scrollToHighlight]);
  
  // Separate highlights into style-specific and general literary techniques
  const styleSpecificItems = legendItems.filter(item => item.highlight.is_style_specific);
  const generalItems = legendItems.filter(item => !item.highlight.is_style_specific);
  
  const hasStyleSpecificItems = styleSpecificItems.length > 0;
  const hasGeneralItems = generalItems.length > 0;

  return (
    <div className="mb-4 pb-3 border-b border-border/50 highlight-legend">
      <h5 className="text-xs font-medium text-muted-foreground mb-2">
        HIGHLIGHTED FEATURES <em className="font-normal">(tap or hover for details)</em>
      </h5>
      
      {/* Render style-specific highlights first if available */}
      {hasStyleSpecificItems && (
        <div className="mb-3">
          <h6 className="text-xs font-medium text-muted-foreground mb-1">Poet's Style Elements</h6>
          <div className="flex flex-wrap gap-3">
            {styleSpecificItems.map((item) => {
              return (
                <LegendItem 
                  key={item.highlight.id}
                  item={item}
                  isActive={activeHighlightId === item.highlight.id}
                  onInteraction={handleHighlightInteraction}
                />
              );
            })}
          </div>
        </div>
      )}
      
      {/* Render general literary techniques */}
      {hasGeneralItems && (
        <div>
          {hasStyleSpecificItems && (
            <h6 className="text-xs font-medium text-muted-foreground mb-1">General Literary Techniques</h6>
          )}
          <div className="flex flex-wrap gap-3">
            {generalItems.map((item) => {
              return (
                <LegendItem 
                  key={item.highlight.id}
                  item={item}
                  isActive={activeHighlightId === item.highlight.id}
                  onInteraction={handleHighlightInteraction}
                />
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

// Helper component for individual legend items
interface LegendItemProps {
  item: LegendHighlightInfo;
  isActive: boolean;
  onInteraction: (highlightId: string, action: 'enter'|'leave'|'click'|'scroll') => void;
}

const LegendItem: React.FC<LegendItemProps> = ({ item, isActive, onInteraction }) => {
  const { highlight, displayLabel, isMultiLine } = item;
  
  return (
    <HighlightTooltip
      content={<HighlightNoteContent highlight={highlight} isOverlapping={false} />}
      position="top"
      delayShow={400}
    >
      <div 
        className={cn(
          "flex items-center gap-2 px-2 py-1 rounded text-xs cursor-pointer transition-colors highlight-item",
          isActive 
            ? "bg-muted/80 ring-1 ring-primary/30 active" 
            : "bg-muted hover:bg-muted/80"
        )}
        onClick={() => {
          onInteraction(highlight.id, 'click');
          onInteraction(highlight.id, 'scroll');
        }}
        onMouseEnter={() => onInteraction(highlight.id, 'enter')}
        onMouseLeave={() => onInteraction(highlight.id, 'leave')}
        onFocus={() => onInteraction(highlight.id, 'enter')}
        onBlur={() => onInteraction(highlight.id, 'leave')}
        tabIndex={0}
        role="button"
        aria-label={`Go to highlight: ${highlight.note || 'No note'}`}
        style={{ 
          borderLeft: `4px solid ${highlight.color || '#ccc'}`,
          backgroundColor: isActive ? hslToHsla(highlight.color || '#ccc', 0.125) : undefined
        }}
      >
        <span 
          className="inline-block w-2.5 h-2.5 rounded-full" 
          style={{ backgroundColor: highlight.color || '#ccc' }}
        />
        <span className="truncate max-w-[150px]">{displayLabel}</span>
        {isMultiLine && (
          <HighlightTooltip
            content="Multi-line highlight"
            position="top"
            delayShow={300}
          >
            <AlignJustify className="w-3 h-3 text-muted-foreground ml-1" />
          </HighlightTooltip>
        )}
      </div>
    </HighlightTooltip>
  );
};

export default HighlightLegend; 